import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  AppContext,
  FeatureFlags,
  GlobalUser,
  Operator,
  Permissions,
} from '../models';
import { logoutAndRedirectToLogin, readAppContext } from '../utils';
import {
  FspUserCulture,
  MerchantAccountStatus,
  MerchantAccountType,
} from '@fsp-lib/shared/enums';
import { MerchantAccount } from '@fsp-lib/shared/models';

@Injectable({
  providedIn: 'root',
})
export class ContextService {
  appContext$ = new BehaviorSubject<AppContext>(readAppContext());

  private get appContext(): AppContext {
    if (!this.appContext$.value) {
      logoutAndRedirectToLogin();
      throw new Error('Missing app context');
    }
    return this.appContext$.value;
  }

  private set appContext(newContext: AppContext) {
    this.appContext$.next(newContext);
  }

  adminOnBasic(): boolean {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      return (
        !currentAppContext.operator.isInTrial &&
        currentAppContext.operator.isAdmin &&
        this.isBasicPlan()
      );
    }
    return false;
  }

  getAppContext(): AppContext {
    return this.appContext;
  }

  getFeatureFlags(): FeatureFlags {
    return this.appContext.featureFlags;
  }

  getIcaoIdentifier(locationId: number): string {
    if (locationId == null) {
      return null;
    }
    const location = this.appContext.operator?.locations?.find(
      (l) => l.locationId === locationId,
    );
    const isValidIdentifier = !!location?.icaoLocationId;
    if (isValidIdentifier) {
      return location.locationIdentifier;
    }
    return null;
  }

  getOperatorId(): number {
    return this.appContext.operator?.operatorId;
  }

  getOperator(): Operator {
    return this.appContext.operator;
  }

  getPermissions(): Permissions {
    return this.appContext.permissions;
  }

  getUserCulture(): string {
    return (
      this.appContext.user?.culture ||
      this.appContext.operator?.culture ||
      FspUserCulture.EnglishUS
    );
  }

  getUserLabelSingular(): string {
    return this.getOperator().userLabel?.slice(0, -1);
  }

  getUser(): GlobalUser {
    return this.appContext.user;
  }

  hasMerchantAccount(): boolean {
    return this.hasPayrixAccount() || this.hasPropayAccount();
  }

  hasPayrixAccount(): boolean {
    return (
      this.appContext.merchantAccounts.find(
        (merchantAccount) =>
          merchantAccount.typeId === MerchantAccountType.Payrix,
      )?.statusId === MerchantAccountStatus.Active
    );
  }

  hasPropayAccount() {
    return (
      this.appContext.merchantAccounts.find(
        (merchantAccount) =>
          merchantAccount.typeId === MerchantAccountType.ProPay,
      )?.statusId === MerchantAccountStatus.Active
    );
  }

  isBasicPlan(): boolean {
    return !this.isProPlanOrHigher();
  }

  isPayrixEnabled(): boolean {
    return this.appContext.merchantAccounts.some(
      (merchantAccount) =>
        merchantAccount.typeId === MerchantAccountType.Payrix &&
        merchantAccount.isEnabled,
    );
  }

  isPropayEnabled(): boolean {
    return this.appContext.merchantAccounts.some(
      (merchantAccount) =>
        merchantAccount.typeId === MerchantAccountType.ProPay &&
        merchantAccount.isEnabled,
    );
  }

  isProPlanOrHigher(): boolean {
    //NOTE: all plans are now Pro or Higher
    return true;
  }

  isQaEnvironment(): boolean {
    const qaEnvironments = ['qa', 'integration'];
    return qaEnvironments.includes(this.appContext.fspApiEnvironment);
  }

  updateAviatorProBaseWebUrl(newAviatorProBaseWebUrl: string): void {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      this.appContext = {
        ...currentAppContext,
        aviatorProBaseWebUrl: newAviatorProBaseWebUrl,
      };
    }
  }

  updateContext(newConfig: AppContext): void {
    this.appContext = newConfig;
  }

  updateMerchantAccounts(newMerchantAccounts: MerchantAccount[]) {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      this.appContext = {
        ...currentAppContext,
        merchantAccounts: newMerchantAccounts.filter(
          (account) => account.isEnabled,
        ),
      };
    }
  }

  updateOperator(newOperator: Operator): void {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      this.appContext = {
        ...currentAppContext,
        operator: newOperator,
      };
    }
  }

  updatePermissions(newPermissions: Permissions): void {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      this.appContext = {
        ...currentAppContext,
        permissions: newPermissions,
      };
    }
  }

  updateStorefrontEnabled(newStorefrontEnabled: boolean): void {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      this.appContext = {
        ...currentAppContext,
        storefrontEnabled: newStorefrontEnabled,
      };
    }
  }

  updateUser(newUser: GlobalUser): void {
    const currentAppContext = this.getAppContext();
    if (currentAppContext) {
      this.appContext = {
        ...currentAppContext,
        user: newUser,
      };
    }
  }
}
